/**
 * MyAttendance — employee-facing attendance view (v109.9.28).
 *
 * Replaces the old "Recent Punch History (last 60 days)" + "Monthly View"
 * grid with the SAME cube grid used inside Payroll → Attendance.
 *
 * Workflow (per user spec):
 *   • Draft state       — read-only cubes.
 *   • Published state   — each cube exposes ✎ pencils for Punch-In, Punch-Out,
 *                         and the Report tile. Every edit REQUIRES a remark.
 *   • Submit for approval → acknowledgement dialog ("I have shared True &
 *     Correct Information..."). Only after ticking the acceptance
 *     checkbox can the employee submit.
 *
 * Stat card is shown at the TOP (spec). User edits do not update the
 * stat card totals (server recomputes only after admin approves).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Clock, CheckCircle2, XCircle, Pencil, FileText, Send, ShieldAlert, BookOpen, RefreshCw,
} from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { MasterUpdateTag } from "../components/attendance/MasterUpdateTag";
import { classifyStatus, statusCellClasses, statusToCode, codeToLabel, fmtHoursHM, isPastOrToday } from "../lib/attendanceStatus";
import { fmtDatePR, fmtTimePR as fmt12h, fmtDateTimePR } from "../lib/datetime";
import { AmPmTimeInput } from "../components/AmPmTimeInput";
import PunchWidget from "../components/PunchWidget";
import AttendancePolicyDialog from "../components/AttendancePolicyDialog";
import LeaveStateTag from "../components/LeaveStateTag";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const ACK_TEXT = "I have shared True & Correct Information, Admin have a right to deduct my salary on actual Ground, If the Remark is Not Satisfactory Admin Can Reject my request. I accept.";

export default function MyAttendance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [calc, setCalc] = useState(null);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editField, setEditField] = useState(null); // { day, field }
  const [submitAck, setSubmitAck] = useState(false); // Dialog open flag
  const [ackChecked, setAckChecked] = useState(false);
  const [reportView, setReportView] = useState(null); // day whose report is being viewed
  // v109.9.124 — Attendance Policy gate. Auto-open until acknowledged.
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyAck, setPolicyAck] = useState({ loaded: false, acknowledged: false });

  useEffect(() => {
    api.get("/attendance-policy/acknowledgment")
      .then(({ data }) => {
        const acked = !!data?.acknowledged;
        setPolicyAck({ loaded: true, acknowledged: acked });
        if (!acked) setPolicyOpen(true);
      })
      .catch(() => setPolicyAck({ loaded: true, acknowledged: false }));
  }, []);

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get(`/payroll/salary-calc?employee_id=${user.id}&year=${year}&month=${month}`),
        api.get(`/payroll/attendance/period?employee_id=${user.id}&year=${year}&month=${month}`),
      ]);
      setCalc(c.data);
      setPeriod(p.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year, month, user?.id]);

  const canEdit = period && ["published", "employee_edits_requested"].includes(period.state);
  const alreadySubmitted = period && ["employee_submitted", "finalised"].includes(period.state);

  const submitForApproval = async () => {
    if (!ackChecked) { toast.error("Please tick the acceptance box."); return; }
    try {
      await api.post("/payroll/attendance/submit-for-approval", { year, month, acknowledged: true });
      toast.success("Submitted for approval");
      setSubmitAck(false); setAckChecked(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const c = calc?.counts || {};

  return (
    <div className="max-w-[1500px] space-y-5" data-testid="my-attendance-page">
      <PageHeader
        moduleKey="team_attendance"
        title="My Attendance"
        subtitle="Daily punch, report, comp-off & leave — for the selected month"
        icon={Clock}
        tone="sky"
        eyebrow="Personal · Attendance"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPolicyOpen(true)}
              className="h-9 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/30"
              data-testid="myatt-policy-btn"
              title="Open Attendance Policy"
            >
              <BookOpen className="w-4 h-4 mr-1.5" /> Policy
            </Button>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-28" data-testid="myatt-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="myatt-year-input" />
          </div>
        }
      />

      {/* Dashboard Attendance Bar — same Punch In/Out + Daily Report controls as home dashboard */}
      <PunchWidget onChange={load} />

      {/* Period state banner */}
      {period && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 flex items-center gap-2 flex-wrap">
          <Badge
            data-testid="myatt-period-state"
            className={
              period.state === "finalised" ? "bg-emerald-100 text-emerald-800" :
              period.state === "employee_submitted" ? "bg-indigo-100 text-indigo-800" :
              period.state === "employee_edits_requested" ? "bg-amber-100 text-amber-800" :
              period.state === "published" ? "bg-sky-100 text-sky-800" :
              "bg-slate-200 text-slate-700"
            }
          >
            State: {period.state}
          </Badge>
          {/* v109.9.136 — Recheck: re-derive status codes from latest punches + leaves */}
          <Button
            size="sm" variant="outline" onClick={() => load()}
            className="h-7 text-[10.5px]"
            data-testid="myatt-recheck-btn"
            title="Recompute status codes from punches + approved leaves"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recheck Status
          </Button>
          {alreadySubmitted && (
            <div className="text-[12px] text-slate-500 italic">
              Already submitted — awaiting admin action.
            </div>
          )}
          {/* v109.9.148 — Banners for auto-submit-after-5th & Master-Admin force-calculate */}
          {period.auto_submitted && (
            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10.5px]" title={period.auto_submitted_reason} data-testid="myatt-auto-submitted-badge">
              Auto-submitted (past the 5th) — no employee action recorded.
            </Badge>
          )}
          {period.force_calculated && (
            <Badge className="bg-purple-100 text-purple-800 border border-purple-300 text-[10.5px]" title={period.force_calculated_reason} data-testid="myatt-force-calc-badge">
              Force-calculated by {period.force_calculated_by_name || "Master Admin"}
            </Badge>
          )}
          {canEdit && (
            <>
              <div className="text-[12px] text-slate-500 flex-1">
                Tap ✎ on any tile to file a per-field change (remark required). When you&apos;re done, click <strong>Calculate My Salary</strong>.
              </div>
              <Button
                onClick={() => { setAckChecked(false); setSubmitAck(true); }}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="myatt-submit-approval-btn"
              >
                <Send className="w-4 h-4 mr-1.5" /> Calculate My Salary
              </Button>
            </>
          )}
          {!canEdit && !alreadySubmitted && (
            <div className="text-[12px] text-slate-500 italic">This month has not been published to you yet.</div>
          )}
        </div>
      )}

      {loading && <div className="p-6 text-center text-slate-400">Loading…</div>}

      {/* Stat card at the TOP (spec). Server-computed — does not update
          on user edits until admin approves. */}
      {!loading && calc && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="myatt-stat-top">
          <StatBox label="Working Days" value={calc.working_days} />
          <StatBox label="Present" value={calc.counts?.present ?? 0} tone="emerald" />
          <StatBox label="Absent" value={calc.counts?.absent ?? 0} tone="rose" />
          <StatBox label="Paid Leave" value={calc.counts?.paid_leave ?? 0} tone="sky" />
          <StatBox label="Half Days" value={calc.counts?.half ?? 0} tone="amber" />
          <StatBox label="Holiday Worked" value={calc.counts?.holiday_worked ?? 0} tone="purple" />
          <StatBox label="Reports Missing" value={calc.counts?.reports_missing ?? 0} tone="rose" />
          <StatBox label="Late Punch-ins" value={calc.counts?.late_punchins ?? 0} tone="amber" />
          <StatBox label="Edits Last Month" value={calc.counts?.edits_last_month ?? 0} tone="sky" />
          <StatBox label="Total Edits (till this month)" value={calc.counts?.edits_total_till_month ?? 0} tone="sky" />
        </div>
      )}

      {/* Cube grid */}
      {!loading && calc && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="myatt-tile-grid">
          {(() => {
            const visible = calc.per_day.filter(isPastOrToday);
            return visible.length > 0 ? visible : calc.per_day;
          })().map((d) => (
            <MyCube
              key={d.date} d={d}
              canEdit={canEdit && !alreadySubmitted}
              pendingEdits={(period?.edit_requests || []).filter((e) => e.date === d.date && e.status === "pending")}
              onEdit={(field) => setEditField({ day: d, field })}
              onOpenReport={(day) => setReportView(day)}
              onOpenNewReport={(day) => navigate(`/m/hr_daily_report?new=${day.date}`)}
            />
          ))}
        </div>
      )}

      {/* Per-field edit dialog (Punch In / Punch Out / Report / Status) */}
      <FieldEditDialog
        open={!!editField}
        onClose={() => setEditField(null)}
        editField={editField}
        year={year}
        month={month}
        onSaved={load}
      />

      {/* Report view dialog */}
      <Dialog open={!!reportView} onOpenChange={(v) => { if (!v) setReportView(null); }}>
        <DialogContent className="max-w-2xl" data-testid="myatt-report-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Daily Report — {fmtDatePR(reportView?.date)} · {reportView?.weekday}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-[13px]">
            {reportView?.report_submitted_at && (
              <div className="text-[11px] text-slate-500">
                Submitted at {fmtDateTimePR(reportView.report_submitted_at)}
              </div>
            )}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 whitespace-pre-wrap min-h-[120px] text-slate-800 dark:text-slate-100" data-testid="myatt-report-view-content">
              {reportView?.report_content || <span className="italic text-slate-400">No content on record.</span>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportView(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.129 — Calculate My Salary acknowledgement dialog with Yes / No */}
      <Dialog open={submitAck} onOpenChange={setSubmitAck}>
        <DialogContent className="max-w-lg" data-testid="myatt-ack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" /> Calculate My Salary — Confirmation
            </DialogTitle>
          </DialogHeader>
          <div className="text-[13.5px] text-amber-900 dark:text-amber-100 leading-relaxed border-2 border-amber-400 rounded-lg bg-amber-50 dark:bg-amber-950/30 p-4 font-semibold">
            Once submitted, <b>no one can make any changes to your attendance</b> for this month, and salary will be calculated based on the current data.
            <br /><br />
            Do you agree?
          </div>
          <div className="text-[11.5px] text-slate-500 italic mt-1">
            {ACK_TEXT}
          </div>
          <label className="flex items-center gap-2 text-[12.5px] font-semibold mt-2">
            <input
              type="checkbox"
              checked={ackChecked}
              onChange={(e) => setAckChecked(e.target.checked)}
              data-testid="myatt-ack-checkbox"
            />
            I agree — I have shared true &amp; correct information.
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSubmitAck(false)} data-testid="myatt-ack-no">
              No, Go Back
            </Button>
            <Button
              onClick={submitForApproval}
              disabled={!ackChecked}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="myatt-ack-submit"
            >
              <Send className="w-4 h-4 mr-1.5" /> Yes, Calculate My Salary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* v109.9.124 — Attendance Policy dialog. Force-open until acknowledged. */}
      <AttendancePolicyDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        forceOpen={policyAck.loaded && !policyAck.acknowledged}
        onAcknowledged={() => setPolicyAck({ loaded: true, acknowledged: true })}
      />
    </div>
  );
}

// -------------------------------------------------------------
// Employee-side cube tile
// -------------------------------------------------------------
function MyCube({ d, canEdit, pendingEdits, onEdit, onOpenReport, onOpenNewReport }) {
  const flags = classifyStatus(d);
  const { isSunday, isSunWorked, isPureHoliday } = flags;
  const { container: cls, chip: chipCls } = statusCellClasses(flags);

  const fmt = fmt12h;

  // Sunday no-activity → still allow editing (v109.9.148 — employees can request
  // WO/PH edits, e.g. mark it as a Public Holiday, mark themselves worked, etc.)
  if (isSunday && !isSunWorked) {
    return (
      <div className={`rounded-xl border-2 p-4 ${cls}`} data-testid={`myatt-cube-${d.date}`}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-[20px] font-extrabold">{fmtDatePR(d.date)}</div>
            <div className="text-[13px] font-semibold text-slate-700">{d.weekday}</div>
          </div>
          {canEdit && (
            <button
              onClick={() => onEdit("status")}
              className="opacity-60 hover:opacity-100 transition-opacity"
              title="Request status change (WO / PH override)"
              data-testid={`myatt-edit-status-${d.date}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className={`inline-block px-2 py-1 rounded font-bold text-[11px] uppercase ${chipCls}`} title="Weekly Off — Paid">
          <span className="font-mono mr-1">WO</span>· Weekly Off · Paid
        </div>
        {(pendingEdits || []).filter((e) => e.field === "status").map((e, i) => (
          <div key={i} className="text-[10.5px] rounded-md bg-orange-50 border border-orange-300 px-1.5 py-1 mt-2">
            <div className="grid grid-cols-2 gap-1">
              <div><span className="text-slate-500 font-bold uppercase text-[8.5px]">Orig:</span> <span className="line-through">{d.status}</span></div>
              <div><span className="text-orange-700 font-bold uppercase text-[8.5px]">Edited:</span> <span className="text-orange-700 font-bold">{e.proposed?.status || "-"}</span></div>
            </div>
            <div className="text-slate-600 italic truncate">&ldquo;{e.reason}&rdquo;</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border-2 p-4 ${cls}`} data-testid={`myatt-cube-${d.date}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[20px] font-extrabold leading-none">{fmtDatePR(d.date)}</div>
          <div className="text-[13px] font-semibold text-slate-700 mt-0.5">{d.weekday}</div>
        </div>
      </div>

      <MasterUpdateTag day={d} testid={`myatt-master-update-${d.date}`} />
      {/* Status chip — click to file a status-edit request */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className={`inline-block px-2 py-1 rounded font-bold text-[11px] uppercase ${chipCls}`} title={d.status}>
          <span className="font-mono mr-1 font-bold" data-testid={`myatt-code-${d.date}`}>{statusToCode(d.status, d)}</span>
          <span className="text-slate-400 mx-0.5">|</span> {codeToLabel(statusToCode(d.status, d))}
        </div>
        <LeaveStateTag
          state={d.leave_state}
          emergency={d.leave_is_emergency}
          autoGenerated={d.leave_auto_generated}
          autoConvertedLwp={d.leave_auto_converted_to_lwp}
        />
        {canEdit && (
          <button
            onClick={() => onEdit("status")}
            className="opacity-60 hover:opacity-100 transition-opacity"
            title="Request status change"
            data-testid={`myatt-edit-status-${d.date}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {/* v109.9.148 — Original strike-through + Edited (orange) inline for status */}
      {(pendingEdits || []).filter((e) => e.field === "status").map((e, i) => (
        <div key={i} className="text-[10.5px] rounded-md bg-orange-50 border border-orange-300 px-1.5 py-1 mb-2" data-testid={`myatt-status-diff-${d.date}`}>
          <div className="grid grid-cols-2 gap-1">
            <div><span className="text-slate-500 font-bold uppercase text-[8.5px]">Orig:</span> <span className="line-through text-slate-500">{d.status}</span></div>
            <div><span className="text-orange-700 font-bold uppercase text-[8.5px]">Edited:</span> <span className="text-orange-700 font-bold">{e.proposed?.status || "-"}</span></div>
          </div>
          <div className="text-slate-600 italic truncate">&ldquo;{e.reason}&rdquo;</div>
        </div>
      ))}

      {isSunWorked && (
        <div className="mb-2">
          {d.comp_off_status === "approved" ? (
            <div className="inline-flex items-center gap-1.5 rounded-md bg-purple-600 text-white px-2 py-1 text-[11px] font-bold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Comp Off Approved · {d.comp_off_accrual} day{(d.comp_off_accrual || 0) > 1 ? "s" : ""}
            </div>
          ) : d.comp_off_status === "rejected" ? (
            <div className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 text-white px-2 py-1 text-[11px] font-bold">
              <XCircle className="w-3.5 h-3.5" /> Comp Off Rejected
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 text-white px-2 py-1 text-[11px] font-bold">
              <Clock className="w-3.5 h-3.5" /> Pending Comp-Off Approval · {d.comp_off_accrual} day
            </div>
          )}
        </div>
      )}

      {/* Punch In / Punch Out with original + edited value shown when pending */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <FieldTile
          label="Punch In" value={fmt(d.punch_in)} missing={!d.punch_in}
          canEdit={canEdit} onEdit={() => onEdit("punch_in")}
          pending={(pendingEdits || []).find((e) => e.field === "punch_in")}
          formatValue={fmt}
          testid={`myatt-edit-in-${d.date}`}
        />
        <FieldTile
          label="Punch Out" value={fmt(d.punch_out)} missing={!d.punch_out}
          canEdit={canEdit} onEdit={() => onEdit("punch_out")}
          pending={(pendingEdits || []).find((e) => e.field === "punch_out")}
          formatValue={fmt}
          testid={`myatt-edit-out-${d.date}`}
        />
      </div>

      {/* Working duration */}
      {d.hours_worked > 0 && (
        <div className="text-[12px] font-mono font-bold text-emerald-700 mb-2">
          <Clock className="w-3 h-3 inline mr-1" />Worked {fmtHoursHM(d.hours_worked)}
        </div>
      )}

      {/* Daily report */}
      <div className="mb-2">
        <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5">Daily Report</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {d.report_submitted ? (
            <button
              onClick={() => onOpenReport?.(d)}
              className="inline-flex items-center gap-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-[11px] font-bold transition-colors"
              data-testid={`myatt-view-report-${d.date}`}
              title="View submitted report"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Submitted · view
            </button>
          ) : (
            <div className="inline-flex items-center gap-1 rounded bg-rose-100 text-rose-700 px-2 py-1 text-[11px] font-bold border border-rose-300">
              <XCircle className="w-3.5 h-3.5" /> Not Filled
            </div>
          )}
          {canEdit && (
            <button
              onClick={() => {
                if (!d.report_submitted && onOpenNewReport) {
                  // v109.9.148 — Route to Daily Report module's New Report dialog
                  onOpenNewReport(d);
                } else {
                  onEdit("report");
                }
              }}
              className="inline-flex items-center gap-0.5 rounded border border-sky-300 text-sky-700 px-2 py-1 text-[10.5px] font-bold hover:bg-sky-50"
              data-testid={`myatt-edit-report-${d.date}`}
              title={d.report_submitted ? "Request edit to submitted report" : "Open Daily Report form"}
            >
              <FileText className="w-3 h-3" /> {d.report_submitted ? "Fix" : "Submit"}
            </button>
          )}
        </div>
        {/* Show timestamp when report submitted */}
        {d.report_submitted && d.report_submitted_at && (
          <div className="text-[10.5px] text-slate-500 mt-0.5 italic" data-testid={`myatt-report-time-${d.date}`}>
            Submitted at {fmtDateTimePR(d.report_submitted_at)}
          </div>
        )}
        {/* Pending report edit — orig vs edited */}
        {(pendingEdits || []).filter((e) => e.field === "report").map((e, i) => (
          <div key={i} className="text-[10.5px] rounded-md bg-orange-50 border border-orange-300 px-1.5 py-1 mt-1">
            <div className="grid grid-cols-1 gap-0.5">
              <div><span className="text-slate-500 font-bold uppercase text-[8.5px]">Orig:</span> <span className="italic text-slate-500">{(d.report_content || "").slice(0, 60) || "(empty)"}{(d.report_content || "").length > 60 ? "…" : ""}</span></div>
              <div><span className="text-orange-700 font-bold uppercase text-[8.5px]">Edited:</span> {(e.proposed?.report || "").slice(0, 60) || "(empty)"}{(e.proposed?.report || "").length > 60 ? "…" : ""}</div>
            </div>
            <div className="text-slate-600 italic truncate">&ldquo;{e.reason}&rdquo;</div>
          </div>
        ))}
      </div>

      {d.leave_type && (
        <div className="mb-1">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${d.leave_is_unpaid ? "bg-orange-600 text-white" : d.leave_is_half_day ? "bg-yellow-500 text-black" : "bg-slate-500 text-white"}`}>
            {d.leave_type}
          </span>
        </div>
      )}

      {/* Existing pending edit requests on this date */}
      {(pendingEdits || []).length > 0 && (
        <div className="mt-2 space-y-1">
          {pendingEdits.map((e, i) => (
            <div key={i} className="text-[10.5px] rounded-md bg-amber-50 border border-amber-300 px-1.5 py-1">
              <span className="font-bold text-amber-800 uppercase text-[9px]">Pending · {e.field || "edit"}</span>
              <div className="text-slate-600 italic truncate">&ldquo;{e.reason}&rdquo;</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldTile({ label, value, missing, canEdit, onEdit, pending, formatValue, testid }) {
  const proposedValue = pending && formatValue
    ? formatValue(pending.proposed?.[label.toLowerCase().replace(/ /g, "_")])
    : pending?.proposed?.[label.toLowerCase().replace(/ /g, "_")];
  return (
    <div className="rounded-md bg-white/60 border border-slate-200 p-2 relative">
      <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5">{label}</div>
      {missing ? (
        <div className={`font-bold text-[11px] ${pending ? "text-slate-500 line-through" : "text-rose-600"}`}>Not Marked</div>
      ) : (
        <div className={`font-mono text-[13px] font-bold ${pending ? "text-slate-500 line-through" : "text-emerald-700"}`}>{value}</div>
      )}
      {pending && (
        <div className="mt-1 text-[11px] rounded-sm bg-orange-50 border border-orange-300 px-1.5 py-1 leading-tight" data-testid={`${testid}-diff`}>
          <div className="text-[8.5px] uppercase font-bold text-orange-700">Edited</div>
          <div className="font-mono font-bold text-orange-700">{proposedValue || "—"}</div>
          <div className="text-[10px] text-slate-500 italic truncate">&ldquo;{pending.reason}&rdquo;</div>
        </div>
      )}
      {canEdit && (
        <button
          onClick={onEdit}
          className="absolute top-1 right-1 opacity-60 hover:opacity-100"
          title="Request edit"
          data-testid={testid}
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// -------------------------------------------------------------
// Per-field edit-request dialog — remark is MANDATORY.
// -------------------------------------------------------------
function FieldEditDialog({ open, onClose, editField, year, month, onSaved }) {
  const [proposed, setProposed] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!editField) return;
    setRemark("");
    // Prefill proposed with current value.
    const d = editField.day;
    if (editField.field === "punch_in") setProposed(d.punch_in ? String(d.punch_in).slice(11, 16) : "");
    else if (editField.field === "punch_out") setProposed(d.punch_out ? String(d.punch_out).slice(11, 16) : "");
    else if (editField.field === "report") setProposed(d.report_content || "");
    else if (editField.field === "status") setProposed(d.status || "");
    else setProposed("");
  }, [editField]);

  const submit = async () => {
    if (remark.trim().length < 3) { toast.error("Remark is required (min 3 chars)"); return; }
    setBusy(true);
    try {
      const payload = {
        year, month, date: editField.day.date,
        field: editField.field,
        reason: remark.trim(),
        proposed: { [editField.field]: proposed },
      };
      const res = await api.post("/payroll/attendance/edit-request", payload);
      if (res?.data?.leave_auto_created) {
        if (res.data.auto_converted_to_lwp) {
          toast.warning(res.data.conversion_note || `Leave auto-created & converted to ${res.data.kind_label} (insufficient balance).`, { duration: 8000 });
        } else {
          toast.success(`${res.data.kind_label} leave request sent to Master Admin.`, { duration: 6000 });
        }
      } else {
        toast.success("Edit-request filed");
      }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const label = editField?.field === "punch_in" ? "Punch In" :
                editField?.field === "punch_out" ? "Punch Out" :
                editField?.field === "report" ? "Daily Report content" :
                editField?.field === "status" ? "Status" : "New value";

  const isStatusField = editField?.field === "status";
  const isPunchField = editField?.field?.startsWith("punch_");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="myatt-field-edit-dialog">
        <DialogHeader>
          <DialogTitle>Request Edit — {fmtDatePR(editField?.day?.date)} · {editField?.field?.replace(/_/g, " ")}</DialogTitle>
        </DialogHeader>
        {/* Original value banner */}
        {editField?.day && (
          <div className="rounded-md border border-slate-200 bg-slate-50 dark:bg-slate-900 dark:border-slate-700 px-2 py-1.5 text-[11px]">
            <span className="text-slate-500 font-bold uppercase text-[9px] mr-1">Original:</span>
            <span className="font-mono">
              {editField.field === "punch_in" && (editField.day.punch_in ? fmt12h(editField.day.punch_in) : "Not marked")}
              {editField.field === "punch_out" && (editField.day.punch_out ? fmt12h(editField.day.punch_out) : "Not marked")}
              {editField.field === "report" && (editField.day.report_submitted ? "Submitted" : "Not filled")}
              {editField.field === "status" && editField.day.status}
            </span>
          </div>
        )}
        <div className="space-y-2">
          <div>
            <Label>{label}</Label>
            {editField?.field === "report" ? (
              <Textarea value={proposed} onChange={(e) => setProposed(e.target.value.slice(0, 2000))} rows={4} data-testid="myatt-field-value" placeholder="Enter today's daily-report content…" />
            ) : isStatusField ? (
              <Select value={proposed || ""} onValueChange={setProposed}>
                <SelectTrigger data-testid="myatt-field-value"><SelectValue placeholder="Pick a status" /></SelectTrigger>
                <SelectContent>
                  {/* v109.9.136 — Canonical status set only. */}
                  <SelectItem value="P">P — Present</SelectItem>
                  <SelectItem value="A">A — Absent</SelectItem>
                  <SelectItem value="HD-A">HD-A — Half Day Present</SelectItem>
                  <SelectItem value="WO">WO — Weekly Off</SelectItem>
                  <SelectItem value="PH">PH — Public Holiday</SelectItem>
                  <SelectItem value="CL">CL — Casual Leave (auto-gen leave req)</SelectItem>
                  <SelectItem value="SL">SL — Sick Leave (auto-gen leave req)</SelectItem>
                  <SelectItem value="EL">EL — Earned Leave (auto-gen leave req)</SelectItem>
                  <SelectItem value="LWP">LWP — Leave Without Pay (auto-gen)</SelectItem>
                  <SelectItem value="HD-CL">HD-CL — Half Day Casual (auto-gen)</SelectItem>
                  <SelectItem value="HD-SL">HD-SL — Half Day Sick (auto-gen)</SelectItem>
                  <SelectItem value="HD-EL">HD-EL — Half Day Earned (auto-gen)</SelectItem>
                  <SelectItem value="HD-LWP">HD-LWP — Half Day LWP (auto-gen)</SelectItem>
                </SelectContent>
              </Select>
            ) : isPunchField ? (
              <AmPmTimeInput value={proposed} onChange={setProposed} testid="myatt-field-value" />
            ) : (
              <Input value={proposed} onChange={(e) => setProposed(e.target.value)} data-testid="myatt-field-value" />
            )}
          </div>
          <div>
            <Label>Remark <span className="text-rose-500">*</span></Label>
            <Textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value.slice(0, 500))}
              placeholder="Why is this change needed? (mandatory)"
              data-testid="myatt-field-remark"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || remark.trim().length < 3}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="myatt-field-submit"
          >
            <Send className="w-4 h-4 mr-1.5" /> File Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Local StatBox (mirrors the one in PayrollTabs so we don't cross-import).
function StatBox({ label, value, tone = "slate" }) {
  const cls = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    purple: "bg-purple-50 border-purple-200 text-purple-900",
  }[tone];
  return (
    <div className={`rounded-lg border p-2.5 ${cls}`}>
      <div className="text-[9.5px] uppercase font-bold opacity-80">{label}</div>
      <div className="text-[16px] font-extrabold mt-0.5">{value}</div>
    </div>
  );
}
